import { Grid, OutlinedInput, Switch, Tooltip, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React from 'react';
import Editor from 'react-simple-code-editor';

import {
  AllNetworkStreamTypes,
  NetworkStreamType,
  networkStreamTypeToString,
} from '~/src/dash/api/network';
import { NewIcon } from '~/src/dash/components/icons/new-icon';
import PillSwitcher, { IPillItem } from '~/src/dash/components/pill-switcher';
import { IWizardStepProps } from '~/src/dash/components/wizard';
import WizardButtons from '~/src/dash/components/wizard-buttons';
import { Col, Cols, Panel, Row, Rows, Text } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { extractRouteParams, navigate, RouteNetworkList } from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import { highlight, languages } from '~/src/prismjs';
import { EBool } from '~/src/dash/api/types/bool';

const STREAM_TYPE_PILLS: IPillItem[] = AllNetworkStreamTypes.map(
  (streamType) =>
    ({
      text: networkStreamTypeToString(streamType),
    }) as IPillItem,
);

/**
 * All these props are state variables passed from
 * the parent component (network-settings-view)
 */
interface IBasicNetworkSettingsFormProps extends IWizardStepProps {
  // Helper
  isSuper: () => boolean;
  canEdit: () => boolean;

  // Network Name
  Name: string;
  handleNameChange: (name: string) => void;

  // Stream Type Select
  StreamType: NetworkStreamType;
  RenderOnDemand: boolean;
  handleTogglingRenderOnDemand: () => void;
  handleSelectStreamTypeChange: (value: string) => void;

  // Stream Form Inputs (StreamTimeout, InventoryTimeout)
  StreamTimeout: number;
  InventoryTimeout: number;
  handleStreamFormInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  // HLS Stream URL
  StreamURL: string;
  handleURLEditorChange: (text: string) => void;

  EnableRenderRateGuard?: EBool;
  handleEnableRenderRateGuard?: () => void;
  PulseDetectionPeriod?: number;

  // User props
  Session?: ISessionState;
  User?: IUserState;
}

const BasicNetworkSettingsForm = ({
  onNext,
  isSuper,
  canEdit,
  Name,
  handleNameChange,
  StreamType,
  handleSelectStreamTypeChange,
  RenderOnDemand,
  handleTogglingRenderOnDemand,
  StreamTimeout,
  InventoryTimeout,
  handleStreamFormInputChange,
  StreamURL,
  handleURLEditorChange,
  Session,
  User,
}: IBasicNetworkSettingsFormProps) => {
  const [hasError, setHasError] = React.useState(Name === '');

  return (
    <>
      <Typography
        style={{
          paddingLeft: '5px',
          marginTop: '0px',
          marginBottom: '1.5rem',
          fontSize: '16px',
          fontWeight: 600,
        }}
      >
        Enter the information below to get started
      </Typography>
      <Panel paper>
        <RenderBasicNetworkSettings
          isSuper={isSuper}
          canEdit={canEdit}
          Name={Name}
          setHasError={setHasError}
          handleNameChange={handleNameChange}
          StreamType={StreamType}
          handleSelectStreamTypeChange={handleSelectStreamTypeChange}
          StreamTimeout={StreamTimeout}
          InventoryTimeout={InventoryTimeout}
          handleStreamFormInputChange={handleStreamFormInputChange}
          StreamURL={StreamURL}
          handleURLEditorChange={handleURLEditorChange}
          RenderOnDemand={RenderOnDemand}
          handleTogglingRenderOnDemand={handleTogglingRenderOnDemand}
        />
      </Panel>
      <WizardButtons
        onNext={onNext}
        onBack={() =>
          navigate(RouteNetworkList, extractRouteParams({ session: Session, user: User }))
        }
        disableNextButton={hasError}
      />
    </>
  );
};

interface IRenderBasicNetworkSettingsProps extends IBasicNetworkSettingsFormProps {
  setHasError?: (hasError: boolean) => void;
}

export const RenderBasicNetworkSettings = ({
  isSuper,
  canEdit,
  setHasError,
  Name,
  handleNameChange,
  StreamType,
  handleSelectStreamTypeChange,
  RenderOnDemand,
  handleTogglingRenderOnDemand,
  StreamTimeout,
  InventoryTimeout,
  handleStreamFormInputChange,
  StreamURL,
  handleURLEditorChange,
  EnableRenderRateGuard,
  handleEnableRenderRateGuard,
  PulseDetectionPeriod,
}: IRenderBasicNetworkSettingsProps) => {
  const [nameError, setNameError] = React.useState(false);

  return (
    <Rows largePadding>
      <Row extraSmallHorizontalPadding noVerticalPadding>
        {/* Network Name */}
        <Cols
          style={{
            alignItems: 'center',
          }}
        >
          <Col
            style={{
              minWidth: '336px',
            }}
          >
            <Text bold>Network Name</Text>
          </Col>
          <Col flex>
            <OutlinedInput
              value={Name}
              error={nameError}
              placeholder='Enter custom name'
              margin='dense'
              onChange={(event) => {
                handleNameChange(event.target.value);
                if (event.target.value.trim() === '') {
                  setNameError(true);
                  if (setHasError) {
                    setHasError(true);
                  }
                } else {
                  setNameError(false);
                  if (setHasError) {
                    setHasError(false);
                  }
                }
              }}
              style={{
                minWidth: '100px',
              }}
            />
          </Col>
        </Cols>
      </Row>
      {/* Stream Type */}
      <Row extraSmallHorizontalPadding largeVerticalPadding>
        <Cols
          style={{
            alignContent: 'start',
          }}
        >
          <Col
            style={{
              minWidth: '320px',
            }}
          >
            <Text title bold>
              Stream Type
            </Text>
          </Col>
          <Col>
            <Rows>
              <Row noVerticalPadding>
                <PillSwitcher
                  disabled={!(canEdit() || isSuper())}
                  currentText={
                    StreamType === NetworkStreamType.TYPE_FOD
                      ? 'VOD'
                      : networkStreamTypeToString(StreamType)
                  }
                  pills={STREAM_TYPE_PILLS}
                  onChange={handleSelectStreamTypeChange}
                />
              </Row>
              {[NetworkStreamType.TYPE_VOD, NetworkStreamType.TYPE_FOD].includes(StreamType) && (
                <Row noVerticalPadding>
                  <Cols
                    style={{
                      alignItems: 'center',
                    }}
                  >
                    <Col>
                      <Text bold small>
                        Fetch-On-Demand
                      </Text>
                    </Col>
                    <Col extraSmallHorizontalPadding>
                      <Tooltip
                        title={
                          'Fetch on Demand (FOD) is a cutting edge solution to boost VOD render rate. Traditional VOD streams request all ads at the start, but since most users do not watch content to completion, those ads are never rendered. FOD makes ad requests right before the ad break to minimize ad wastage.'
                        }
                        placement='right-start'
                      >
                        <InfoIcon style={{ width: '16px', color: `${publicaColors.mediumGray}` }} />
                      </Tooltip>
                    </Col>
                    <Col smallHorizontalPadding>
                      <NewIcon />
                    </Col>
                  </Cols>
                </Row>
              )}
              {[NetworkStreamType.TYPE_VOD, NetworkStreamType.TYPE_FOD].includes(StreamType) && (
                <Row noVerticalPadding smallHorizontalPadding>
                  <Switch checked={RenderOnDemand} onChange={handleTogglingRenderOnDemand} />
                </Row>
              )}
            </Rows>
          </Col>
        </Cols>
      </Row>
      {/* Stream Timeout */}
      <Row extraSmallPadding>
        <Cols
          style={{
            alignItems: 'center',
          }}
        >
          <Col
            style={{
              minWidth: '336px',
            }}
          >
            <Text bold>Stream Timeout</Text>
          </Col>
          <Col>
            <OutlinedInput
              name='StreamTimeout'
              value={StreamTimeout}
              placeholder='Integer in ms'
              type='number'
              margin='dense'
              onChange={handleStreamFormInputChange}
            />
          </Col>
          <Col largeHorizontalPadding>
            <Text small>Max duration for a stream to be active</Text>
          </Col>
        </Cols>
      </Row>
      {/* Inventory Timeout */}
      <Row extraSmallPadding>
        <Cols
          style={{
            alignItems: 'center',
          }}
        >
          <Col
            style={{
              minWidth: '336px',
            }}
          >
            <Text bold>Inventory Timeout</Text>
          </Col>
          <Col>
            <OutlinedInput
              name='InventoryTimeout'
              placeholder='Integer in ms'
              type='number'
              margin='dense'
              value={InventoryTimeout}
              onChange={handleStreamFormInputChange}
            />
          </Col>
          <Col largeHorizontalPadding>
            <Text small>Max duration for an inventory to be active</Text>
          </Col>
        </Cols>
      </Row>
      {/* HLS Stream URL for Live Stream Type */}
      {StreamType === NetworkStreamType.TYPE_LIVE && (
        <Row smallVerticalPadding extraSmallHorizontalPadding>
          <Cols
            style={{
              alignContent: 'center',
            }}
          >
            <Col style={{ minWidth: '336px', width: '336px' }}>
              <Text bold>Origin Url</Text>
              <Text mini color={publicaColors.gray}>
                HLS or DASH format - The raw content url to which ads will be inserted.
              </Text>
            </Col>
            <Col flex>
              <Grid
                style={{
                  background:
                    canEdit() || isSuper() ? publicaColors.whiteOrchid : publicaColors.gray,
                  borderRadius: smallRadius,
                  opacity: 1,
                  paddingLeft: 10,
                  paddingRight: 10,
                  width: '100%',
                }}
              >
                <Editor
                  value={StreamURL}
                  highlight={(code) => highlight(code, languages.elm, 'elm')}
                  padding='5px'
                  maxLength={2000}
                  onValueChange={handleURLEditorChange}
                  placeholder='Enter raw HLS stream url'
                />
              </Grid>
            </Col>
          </Cols>
        </Row>
      )}
      {/* Render Rate Guard Toggle */}
      {EnableRenderRateGuard && StreamType === NetworkStreamType.TYPE_LIVE && (
        <Row smallVerticalPadding extraSmallHorizontalPadding>
          <Cols style={{ alignContent: 'center' }}>
            <Col style={{ width: '336px' }}>
              <Text bold>Enable Render Rate Guard</Text>
              <Text mini color={publicaColors.gray}>
                Only available for HLS. Allows ad requests only if the user is engaged with the
                stream.
              </Text>
            </Col>
            <Col flex>
              <Switch
                checked={EnableRenderRateGuard === EBool.EBool_TRUE}
                onChange={handleEnableRenderRateGuard}
              />
            </Col>
          </Cols>
        </Row>
      )}
      {StreamType === NetworkStreamType.TYPE_LIVE && EnableRenderRateGuard === EBool.EBool_TRUE && (
        <Row smallVerticalPadding extraSmallHorizontalPadding>
          <Cols style={{ alignContent: 'center' }}>
            <Col style={{ width: '336px' }}>
              <Text bold>Pulse Detection Period (Seconds)</Text>
            </Col>
            <Col flex>
              <OutlinedInput
                name='PulseDetectionPeriod'
                placeholder='Integer in seconds'
                type='number'
                margin='dense'
                value={PulseDetectionPeriod}
                onChange={handleStreamFormInputChange}
              />
            </Col>
          </Cols>
        </Row>
      )}
    </Rows>
  );
};

export default BasicNetworkSettingsForm;
